/**
 * Фильтры по фактам нод передаются одним query-параметром: записи через `;`
 * в формате `{node_id}.{output_key}[:{item_key}]{op}{value}`.
 * Так не приходится трогать общий сериализатор параметров в apiClient.
 *
 * Здесь же лежит разбор в обратную сторону: пришедшую из графика строку нужно показать
 * контролами панели, иначе фильтр применяется, но невидим и неснимаем.
 */
import { OTHER_CODE } from './nodeChartTheme';

export const ALL_VALUE = 'all';

/** Столько записей фильтра принимает бэкенд — NodeFactFilter.MaxFilters. Лишние он молча отбрасывает. */
export const MAX_NODE_FILTERS = 8;

const KIND_FLAG_MAP = 'boolean_flag_map';
const KIND_EVIDENCE_CLASSIFICATION = 'boolean_evidence_classification';
const KIND_CHECKLIST = 'checklist_results';

export const buildNodeFilterEntry = ({ nodeId, outputKey, itemKey, operator = '=', value }) => {
  const target = itemKey ? `${nodeId}.${outputKey}:${itemKey}` : `${nodeId}.${outputKey}`;
  return `${target}${operator}${value}`;
};

const toValues = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  return value === undefined || value === null ? [] : [value];
};

/**
 * Запись DSL зависит от вида выхода: у флагов значение лежит в `item_key`, у категорий жалоб —
 * в самом `output_key`, у чек-листа сравнение идёт по оценке.
 */
export const buildEntriesForFilter = (filter, rawValue) => {
  const values = toValues(rawValue).filter((value) => value && value !== ALL_VALUE);
  if (values.length === 0) {
    return [];
  }

  const { nodeId, outputKey, kind } = filter;

  if (kind === KIND_FLAG_MAP) {
    // Флаги разложены построчно: item_key = ключ флага, значение — value_bool.
    return values.map((value) =>
      buildNodeFilterEntry({ nodeId, outputKey, itemKey: value, value: 'true' })
    );
  }

  if (kind === KIND_EVIDENCE_CLASSIFICATION && outputKey === '*') {
    // Категории жалоб свёрнуты в один фильтр `{node}.*`, а выбранные коды — это output_key.
    return values.map((value) => buildNodeFilterEntry({ nodeId, outputKey: value, value: 'true' }));
  }

  if (kind === KIND_CHECKLIST) {
    // Отбираем звонки, где пункт провален. Сравнение по коду отбирало бы все звонки,
    // где пункт вообще оценивался, — то есть почти все.
    return values.map((value) =>
      buildNodeFilterEntry({ nodeId, outputKey, itemKey: value, operator: '<=', value: '0.5' })
    );
  }

  return values.map((value) => buildNodeFilterEntry({ nodeId, outputKey, value }));
};

/**
 * Собирает записи фильтра по состоянию контролов и описаниям из каталога.
 * Несколько выбранных значений склеиваются на бэкенде через AND — это «отмечены все выбранные».
 */
export const buildNodeFilterEntries = (values, catalogFilters = []) => {
  if (!values) {
    return [];
  }

  return catalogFilters.flatMap((filter) => buildEntriesForFilter(filter, values[filter.id]));
};

/**
 * Не всякий столбец выражается фильтром. `__other` — свёрнутый хвост; `unknown` приходит из
 * булева агрегата вместо NULL, `—` — из агрегата по кодам там же. Раньше клик по ним открывал
 * журнал с условием, которому не соответствует ни один звонок, то есть пустой список без
 * объяснения.
 */
export const isDrilldownableCode = (code) =>
  Boolean(code) && code !== OTHER_CODE && code !== 'unknown' && code !== '—';

/**
 * Записи фильтра для клика по элементу графика.
 *
 * Карточка каталога описывается тем же контрактом, что и фильтр (`nodeId`/`outputKey`/`kind`),
 * поэтому строится тем же кодом. Своя копия этой логики в карточке разошлась с панелью и у
 * выходов `boolean_flag_map` давала `{node}.{output}={флаг}` вместо `{node}.{output}:{флаг}=true`:
 * сервер сравнивает такое условие с `coalesce(value_code, item_key)`, не глядя на `value_bool`,
 * и в журнал попадали все звонки, которые нода вообще обработала.
 */
export const buildEntriesForChartSelection = (chart, code) => {
  if (!chart || !isDrilldownableCode(code)) {
    return [];
  }

  return buildEntriesForFilter(
    { nodeId: chart.nodeId, outputKey: chart.outputKey, kind: chart.kind },
    [code]
  );
};

const IDENTIFIER_PATTERN = /^[A-Za-z0-9_-]+$/;
const OPERATORS = ['<=', '>=', '!=', '='];

/**
 * Разбор одной записи DSL — зеркало `NodeFactFilter.TryParse` на сервере.
 * Двухсимвольные операторы проверяются первыми, иначе `<=` расщепится по `=`.
 */
export const parseNodeFilterEntry = (entry) => {
  const raw = typeof entry === 'string' ? entry.trim() : '';
  if (!raw) {
    return null;
  }

  const operator = OPERATORS.find((candidate) => raw.indexOf(candidate) > 0);
  if (!operator) {
    return null;
  }

  const index = raw.indexOf(operator);
  const left = raw.slice(0, index);
  const value = raw.slice(index + operator.length);
  if (!value) {
    return null;
  }

  const colon = left.indexOf(':');
  const target = colon >= 0 ? left.slice(0, colon) : left;
  const itemKey = colon >= 0 ? left.slice(colon + 1) : null;

  const dot = target.indexOf('.');
  if (dot <= 0 || dot === target.length - 1) {
    return null;
  }

  const nodeId = target.slice(0, dot);
  const outputKey = target.slice(dot + 1);

  if (!IDENTIFIER_PATTERN.test(nodeId) || !IDENTIFIER_PATTERN.test(outputKey)) {
    return null;
  }

  if (itemKey !== null && !IDENTIFIER_PATTERN.test(itemKey)) {
    return null;
  }

  return { nodeId, outputKey, itemKey, operator, value };
};

/** Значение записи, которое должен показать контрол фильтра, либо null — «каталогом не выражается». */
const resolveSelectionForFilter = (filter, parsed) => {
  const { itemKey, operator, value } = parsed;

  if (filter.kind === KIND_FLAG_MAP) {
    return itemKey && operator === '=' && value === 'true' ? itemKey : null;
  }

  if (filter.kind === KIND_CHECKLIST) {
    // Порог зашит в сборке. Другой порог контролом не выразить, поэтому такую запись
    // не «округляем» до галочки, а сохраняем как есть.
    return itemKey && operator === '<=' && value === '0.5' ? itemKey : null;
  }

  if (itemKey || operator !== '=') {
    return null;
  }

  return value;
};

/**
 * Строка `node_filter` → состояние контролов панели.
 *
 * Записи, которые каталог выразить не может (пакет перезалит, карточка скрыта, снятый флаг,
 * другой порог чек-листа), возвращаются в `unmatched` и обязаны быть добавлены к запросу
 * отдельно: молча отброшенное условие расширило бы выборку журнала относительно графика.
 */
export const parseNodeFilterEntries = (nodeFilter, catalogFilters = []) => {
  const entries = typeof nodeFilter === 'string'
    ? nodeFilter.split(';').map((entry) => entry.trim()).filter(Boolean)
    : [];

  const byId = new Map(catalogFilters.map((filter) => [filter.id, filter]));
  const aggregatedByNode = new Map(
    catalogFilters
      .filter((filter) => filter.outputKey === '*' && filter.kind === KIND_EVIDENCE_CLASSIFICATION)
      .map((filter) => [filter.nodeId, filter])
  );

  const values = {};
  const unmatched = [];

  const select = (filter, selection) => {
    if (filter.control === 'multi_select') {
      const current = Array.isArray(values[filter.id]) ? values[filter.id] : [];
      values[filter.id] = current.includes(selection) ? current : [...current, selection];
      return;
    }

    values[filter.id] = selection;
  };

  entries.forEach((entry) => {
    const parsed = parseNodeFilterEntry(entry);
    if (!parsed) {
      return;
    }

    // Сначала точное совпадение: у свёрнутой карточки жалоб выбранный код — это output_key,
    // поэтому без этого порядка `{node}.{категория}` могло бы разойтись по двум фильтрам.
    const exact = byId.get(`${parsed.nodeId}.${parsed.outputKey}`);
    if (exact) {
      const selection = resolveSelectionForFilter(exact, parsed);
      if (selection !== null) {
        select(exact, selection);
        return;
      }

      unmatched.push(entry);
      return;
    }

    const aggregated = aggregatedByNode.get(parsed.nodeId);
    const isCategoryEntry = aggregated
      && !parsed.itemKey
      && parsed.operator === '='
      && parsed.value === 'true'
      && (aggregated.options ?? []).some((option) => option.code === parsed.outputKey);

    if (isCategoryEntry) {
      select(aggregated, parsed.outputKey);
      return;
    }

    unmatched.push(entry);
  });

  return { values, unmatched };
};

export const serializeNodeFilters = (values, catalogFilters = []) => {
  const entries = buildNodeFilterEntries(values, catalogFilters).slice(0, MAX_NODE_FILTERS);
  return entries.length > 0 ? entries.join(';') : undefined;
};

/**
 * Фильтры каталога, которые можно показать контролами: у остальных либо нет контрола
 * (тексты), либо нечего выбирать. Одно правило на журнал, дашборд и «Сотрудников» —
 * иначе одна и та же панель показывала бы в разных разделах разный набор полей.
 */
export const selectNodeCatalogFilters = (catalog) =>
  (catalog?.filters ?? []).filter(
    (filter) => filter.control !== 'none' && (filter.options?.length ?? 0) > 0
  );

const nonEmptyList = (values) => (Array.isArray(values) && values.length > 0 ? values : undefined);

/**
 * `scope` — срез раздела «Сотрудники» (мультивыбор сотрудников и отделов в шапке). Едет
 * списками, а не одиночным employee_id: тот занят ячейкой тепловой карты, которая открывает
 * примеры своего оператора, и сервер их пересекает, а не подменяет одно другим.
 */
export const buildNodeParams = ({ chart, dateRange, callType, nodeFilter, packVersion, scope }) => ({
  node_id: chart.nodeId,
  output_key: chart.outputKey,
  date_from: dateRange?.from ? formatDate(dateRange.from) : undefined,
  date_to: dateRange?.to ? formatDate(dateRange.to) : undefined,
  call_type: callType && callType !== ALL_VALUE ? callType : undefined,
  node_filter: nodeFilter,
  pack_version: packVersion,
  employee_ids: nonEmptyList(scope?.employeeIds),
  departments: nonEmptyList(scope?.departments)
});

/**
 * Срез диалога примеров по ячейке тепловой карты: «этот пункт у этого оператора».
 *
 * Идентификатор берётся у строки, а не у ячейки. У строки «Без оператора» он пустой Guid, и
 * сервер понимает его как звонки без оператора, а в её ячейках employeeId: null — такой
 * идентификатор в запрос не уходит, и диалог показывал звонки всех сотрудников.
 */
export const buildHeatmapExamplesSelection = ({ item, employee }) => ({
  itemKey: item.code,
  maxScore: 0.5,
  title: `${item.label} · ${employee.name}`,
  employeeId: employee.employeeId
});

const formatDate = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};
